import { IDIOMA_POR_DEFECTO, type Idioma } from './idiomas';
import { diccionario } from './recursos';

export { IDIOMAS, IDIOMA_POR_DEFECTO, NOMBRES_IDIOMA, LOCALE_INTL, esIdiomaSoportado } from './idiomas';
export type { Idioma } from './idiomas';
export { NAMESPACES, clavesDe } from './recursos';
export type { Namespace } from './recursos';

export type Vars = Record<string, string | number>;

/**
 * Idioma activo a nivel de módulo. `SettingsProvider` lo mantiene sincronizado con
 * el ajuste del usuario para que el código que no es un componente de React
 * (helpers de formato, mensajes de error de `lib/`) también pueda traducir.
 */
let idiomaActivo: Idioma = IDIOMA_POR_DEFECTO;

export function fijarIdiomaActivo(idioma: Idioma): void {
    idiomaActivo = idioma;
}

export function idiomaActual(): Idioma {
    return idiomaActivo;
}

const INTERPOLACION = /\{\{\s*(\w+)\s*\}\}/g;

function interpolar(plantilla: string, vars?: Vars): string {
    if (!vars) return plantilla;
    return plantilla.replace(INTERPOLACION, (coincidencia, nombre: string) => {
        const valor = vars[nombre];
        return valor === undefined ? coincidencia : String(valor);
    });
}

/**
 * Resuelve una clave en el idioma pedido. Si falta, cae al castellano; si también
 * falta ahí, devuelve la propia clave (visible en pantalla a propósito: así un
 * hueco de traducción se detecta de un vistazo en vez de pasar como texto vacío).
 *
 * Plurales: si `vars.contador` está presente se prueba antes la variante con
 * sufijo `_uno` (contador === 1) o `_otros` (resto), y si no existe se usa la
 * clave base. Cubre los seis idiomas soportados, todos con plural de tipo
 * uno/otros.
 */
export function traducir(idioma: Idioma, clave: string, vars?: Vars): string {
    const candidatas: string[] = [];
    const contador = vars?.contador;
    if (typeof contador === 'number') {
        candidatas.push(`${clave}${contador === 1 ? '_uno' : '_otros'}`);
    }
    candidatas.push(clave);

    for (const candidata of candidatas) {
        const propio = diccionario(idioma).get(candidata);
        if (propio !== undefined) return interpolar(propio, vars);
    }
    for (const candidata of candidatas) {
        const fallback = diccionario(IDIOMA_POR_DEFECTO).get(candidata);
        if (fallback !== undefined) return interpolar(fallback, vars);
    }
    return clave;
}

/** Traduce con el idioma activo. Para usar fuera de componentes de React. */
export function tr(clave: string, vars?: Vars): string {
    return traducir(idiomaActivo, clave, vars);
}
